<template>
  <OmtCard data-cy="refund-detail-collapsable-card">
    <CFlex>
      <StatusTile
        v-bind="headerData"
        :isTotalVisible="false"
      />
    </CFlex>

    <CDivider
      mt="14px"
      mb="16px"
    />

    <CCollapse :isOpen="isOpen">
      <OmtRow v-if="statusAlert">
        <OmtCard
          mb="16px"
          :bg="statusAlert.bg"
          border="0"
          border-radius="8px"
        >
          <OmtTypography
            as="p1"
            color="var(--colors-light-primary-gray1)"
          >
            <strong>{{ statusAlert.boldText }}</strong> {{ statusAlert.text }}
          </OmtTypography>
        </OmtCard>
      </OmtRow>

      <OmtRow v-if="canceledStatus">
        <OmtCard
          mb="16px"
          :bg="canceledStatus.bg"
          border="0"
          border-radius="8px"
        >
          <OmtTypography
            as="p1"
            color="var(--colors-light-primary-gray1)"
          >
            <strong>{{ canceledStatus.boldText }}</strong>
          </OmtTypography>
          <OmtTypography
            as="p1"
            color="var(--colors-light-primary-gray1)"
          >
            {{ canceledStatus.text }}
          </OmtTypography>
        </OmtCard>
      </OmtRow>

      <CFlex
        v-if="refundDetail.status === REFUND_STATUS.PENDING"
        align="flex-start"
        mb="16px"
      >
        <CBox
          w="24px"
          h="24px"
          border-radius="50%"
          display="flex"
          align-items="center"
          justify-content="center"
          mr="14px"
        >
          <img :src="iconOmint" />
        </CBox>

        <CBox>
          <OmtTypography
            as="h3-bold"
            color="var(--colors-light-primary-gray1)"
            mb="12px"
          >
            Para avançarmos na análise do seu reembolso, solicitamos:
          </OmtTypography>
        </CBox>
      </CFlex>

      <CBox
        mt="-15px"
        pt="16px"
      >
        <CBox v-if="refundDetail.pictures.length">
          <CFlex mb="16px">
            <OmtIcons
              mr="8px"
              size="1x"
              name="image"
            />
            <OmtTypography
              as="h3-bold"
              color="var(--colors-light-primary-gray1)"
            > Documentos enviados </OmtTypography>
          </CFlex>

          <CFlex>
            <CBox
              v-for="(picture, index) in refundDetail.pictures"
              :key="`${picture.source}-${index}`"
              w="32px"
              mr="14px"
            >
              <ImagePreviewer v-bind="picture" />
            </CBox>
          </CFlex>
        </CBox>

        <template v-else>
          <CBox
            v-for="(ticket, ticketIndex) in ticketGroups"
            :key="`${ticket.ticket}-${ticketIndex}`"
            :mb="ticketIndex + 1 < ticketGroups.length ? '24px' : '0'"
          >
            <CBox
              v-for="(refund, refundIndex) in ticket.docs"
              :key="`${refund.ticket}-${refundIndex}`"
              :mb="refundIndex + 1 < ticket.docs.length ? '10px' : '0'"
            >
              <CBox data-cy="card-content">
                <CGrid
                  :templateColumns="['repeat(auto-fit, minmax(100%, max-content))']"
                  justify-content="space-between"
                >
                  <OmtCard
                    bg="var(--colors-light-primary-light-gray)"
                    border="0"
                    border-radius="8px"
                  >
                    <template v-if="refundDetail.showValues && refund.requestedValue">
                      <CFlex
                        :justify-content="!isInAnalysis ? 'space-between' : 'center'"
                        mb="8px"
                        w="100%"
                      >
                        <TextTile
                          v-if="!isInAnalysis"
                          v-chakra
                          v-bind="{ title: refund.requestedValue, as: 'p1-bold' }"
                          color="var(--colors-light-primary-gray1)"
                        />
                        <CFlex v-if="refund.receipts && refund.receipts.length">
                          <OmtButton
                            v-for="(receipt, receiptIndex) in refund.receipts"
                            :key="`${refund.ticket}-${receipt.label}-${receiptIndex}`"
                            height="18px"
                            p="0"
                            leftIcon="arrow-down-to-bracket"
                            variant="ghost"
                            iconColor="var(--colors-light-primary-navy-blue)"
                            :disabled="!receipt.source"
                            @click="download(receipt)"
                          >
                            <OmtTypography
                              ml="-8px"
                              as="button-small"
                              color="var(--colors-light-primary-navy-blue)"
                            >
                              {{ receipt.label }}
                            </OmtTypography>
                          </OmtButton>
                        </CFlex>
                      </CFlex>

                      <CDivider
                        v-if="refund.hasMotives"
                        mt="14px"
                        mb="16px"
                      />
                    </template>

                    <template v-if="refund.hasMotives">
                      <CBox mt="8px">
                        <OmtTypography
                          as="p1-bold"
                          color="var(--colors-light-primary-gray1)"
                          mb="6px"
                        >
                          {{ motivesLabel }}
                        </OmtTypography>

                        <ol class="refund-motives-list">
                          <li
                            v-for="(motivo, motivoIndex) in refund.motives"
                            :key="`${refund.ticket}-${motivoIndex}`"
                          >
                            <OmtTypography
                              as="p1"
                              color="var(--colors-light-primary-gray1)"
                            >
                              {{ motivo }}
                            </OmtTypography>
                          </li>
                        </ol>
                      </CBox>
                    </template>
                  </OmtCard>
                </CGrid>
              </CBox>
            </CBox>
          </CBox>
        </template>

        <slot />

        <template v-if="showComplementaryDocuments">
          <CDivider
            mt="16px"
            mb="16px"
          />

          <OmtRow mb="16px">
            <CBox
              display="flex"
              align-items="center"
            >
              <OmtIcons
                name="image"
                size="1x"
                mr="2"
              />
              <OmtTypography as="h3-bold"> Documentos complementares </OmtTypography>
            </CBox>
          </OmtRow>

          <CFlex flex-wrap="wrap">
            <CBox
              v-for="(picture, index) in complementaryPictures"
              :key="`${picture.source}-${index}`"
              mr="14px"
            >
              <ImagePreviewer
                v-bind="picture"
                class="image-preview"
              />
            </CBox>
          </CFlex>
        </template>
      </CBox>
    </CCollapse>

    <OmtStickedModal
      v-if="showPictureModal"
      @close="triggerPictureModal(false)"
    >
      <CBox
        p="0 16px"
        min-height="80vh"
      >
        <CImage
          data-cy="image-preview-modal"
          :src="sourcePictureModal"
          max-height="100vh"
          max-width="100%"
        />
      </CBox>
    </OmtStickedModal>
  </OmtCard>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { computed, defineComponent, getCurrentInstance, onMounted, ref, toRefs } from "vue";

import ImagePreviewer from "@/components/Molecules/ImagePreviewer";
import TextTile from "@/components/Molecules/TextTile";
import { REFUND_STATUS, REFUND_STATUS_LABELS } from "@/constants/refundStatus";
import { StatusTile } from "./shared";

export default defineComponent({
  name: "RefundDetailCollapsableCard",
  components: {
    TextTile,
    StatusTile,
    ImagePreviewer
  },
  props: {
    refundDetail: {
      type: Object,
      default: () => ({ list: [], pictures: [] })
    },
    cancelPendency: Function,
    isCollapsed: Boolean,
    complementaryPictures: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const { refundDetail, complementaryPictures } = toRefs(props);

    const CANCELLATION_MOTIVE_MESSAGE =
      "SOLICITAÇÃO CANCELADA. O PRAZO PARA ENVIO DAS INFORMAÇÕES COMPLEMENTARES ESGOTOU. ABRA UMA NOVA SOLICITAÇÃO.";

    function isCancellationMotive(motive) {
      return motive.trim().toLowerCase() === CANCELLATION_MOTIVE_MESSAGE.toLowerCase();
    }

    function cleanMotives(doc) {
      return (doc.reasonsPendencieRefused || []).filter(
        motivo => typeof motivo === "string" && motivo.trim().length > 0
      );
    }

    async function loadPicture(id) {
      return await proxy.$store.dispatch("refund/fetchProtocolDetailImage", { documento: id, original: true });
    }

    async function loadPictures() {
      const list = Array.isArray(refundDetail.value.pictures) ? refundDetail.value.pictures : [];

      if (!list.length) {
        refundDetail.value.pictures = [];
        return;
      }

      const loadedPictures = [];

      for (const picture of list) {
        const source = await proxy.$store.dispatch("refund/fetchProtocolDetailImage", {
          documento: picture.id,
          original: false
        });

        loadedPictures.push({
          id: picture.id,
          source,
          loadModalImage: () => loadPicture(picture.id)
        });
      }

      refundDetail.value.pictures = loadedPictures;
    }

    const instance = getCurrentInstance();
    const proxy = instance && instance.proxy ? instance.proxy : null;

    if (!proxy) {
      throw new Error("Refund detail card requires a Vue instance.");
    }

    const isOpen = ref(true);
    const showPictureModal = ref(false);
    const sourcePictureModal = ref("");

    const iconOmint = `${process.env.BASE_URL}images/Group3305.png`;

    const ticketGroups = computed(() => {
      const tickets = Array.isArray(refundDetail.value && refundDetail.value.list) ? refundDetail.value.list : [];

      return tickets
        .map(ticket => {
          const docs = Array.isArray(ticket.docs) ? ticket.docs : [];

          const normalizedDocs = docs
            .map(doc => {
              const motives = cleanMotives(doc);
              const visibleMotives = motives.filter(motivo => !isCancellationMotive(motivo));

              return {
                ...doc,
                motives: visibleMotives,
                hasMotives: visibleMotives.length > 0
              };
            })
            .filter(doc => doc.requestedValue || doc.hasMotives);

          return {
            ...ticket,
            docs: normalizedDocs
          };
        })
        .filter(ticket => ticket.docs.length > 0);
    });

    const headerData = computed(() => {
      const detail = refundDetail.value || {};
      let count = 0;

      for (const ticket of ticketGroups.value) {
        count += ticket.docs.length;
      }

      return {
        count,
        label: detail.description,
        color: detail.color
      };
    });

    const isInAnalysis = computed(() => {
      return headerData.value.label === REFUND_STATUS_LABELS[REFUND_STATUS.IN_ANALYSIS];
    });

    const statusAlert = computed(() => {
      const status = refundDetail.value && refundDetail.value.status;

      const alerts = {
        [REFUND_STATUS.PENDING]: {
          bg: "#FFAF001A",
          boldText: "Responda em até 20 dias",
          text: "ou a solicitação será cancelada."
        }
      };

      return alerts[status] || null;
    });

    const canceledStatus = computed(() => {
      const tickets = Array.isArray(refundDetail.value && refundDetail.value.list) ? refundDetail.value.list : [];

      for (const ticket of tickets) {
        const docs = Array.isArray(ticket && ticket.docs) ? ticket.docs : [];
        for (const doc of docs) {
          const motives = cleanMotives(doc);
          if (motives.some(isCancellationMotive)) {
            return {
              bg: "#C317001A",
              boldText: "Solicitação cancelada.",
              text: "O prazo para envio das informações complementares esgotou. Abra uma nova solicitação."
            };
          }
        }
      }

      return null;
    });

    const motivesLabel = computed(() => {
      const status = refundDetail.value && refundDetail.value.status;

      return status === REFUND_STATUS.PENDING || status === REFUND_STATUS.CANCELLED ? "Pendência:" : "Motivo:";
    });

    const showComplementaryDocuments = computed(() => {
      const hasPictures = Array.isArray(complementaryPictures.value) && complementaryPictures.value.length > 0;
      const description = refundDetail.value && refundDetail.value.description;
      return hasPictures && description !== "Em triagem";
    });

    function triggerPictureModal(value) {
      showPictureModal.value = value;
    }

    async function download(receipt) {
      if (!receipt || !receipt.source) {
        return;
      }

      try {
        proxy.$root.$emit("show-loading");
        sourcePictureModal.value = await proxy.$store.dispatch("refund/fetchProtocolDetailImage", {
          documento: receipt.id,
          original: true
        });

        if (sourcePictureModal.value) {
          triggerPictureModal(true);
          return;
        }
      } finally {
        proxy.$root.$emit("hide-loading");
      }

      await Swal.fire({
        text: "A imagem não está disponível.",
        confirmButtonText: "OK"
      });
    }

    onMounted(() => {
      loadPictures();
    });

    return {
      REFUND_STATUS,
      REFUND_STATUS_LABELS,
      isInAnalysis,
      refundDetail,
      complementaryPictures,
      isOpen,
      showPictureModal,
      sourcePictureModal,
      iconOmint,
      ticketGroups,
      headerData,
      statusAlert,
      motivesLabel,
      showComplementaryDocuments,
      canceledStatus,
      loadPictures,
      loadPicture,
      triggerPictureModal,
      download
    };
  }
});
</script>

<style scoped>
.img-omint {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}

.refund-motives-list {
  margin: 0;
  padding-left: 20px;
}

.refund-motives-list li {
  margin-bottom: 6px;
}

.image-preview {
  width: 32px !important;
  height: 32px !important;
  border-radius: 4px;
  object-fit: contain;
}
</style>
